import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TaskError401Res, tasksApi } from '../api';
import { useRecoilState } from 'recoil';
import { taskErrorSnackBarAtom } from '../recoil';
import { useEffect } from 'react';
import { AxiosError } from 'axios';
import { TaskItem, TodoIntegrationType } from '@demind-inc/core';
import { TasksByDate } from '../types';
import { TEAM_TASKS_QUERY_KEY } from '../queries/teamTasks.shared';
import { translateI18n } from '../../i18n';

interface UseDeleteTaskProps {
  taskId: string;
  boardId: string;
  userId: string;
  mutateParam?: DeleteTaskMutateParam;
}

export interface DeleteTaskMutateParam {
  date: string;
  isOverdue: boolean;
  parentOriginalTaskId?: string;
}

export function useDeleteTask() {
  const queryClient = useQueryClient();
  const [_, setTaskErrorSnackbar] = useRecoilState(taskErrorSnackBarAtom);

  const deleteTaskMutation = useMutation({
    mutationFn: ({ taskId, boardId, userId }: UseDeleteTaskProps) => {
      return tasksApi.deleteTodoTask(taskId, boardId, userId);
    },
    onMutate: async ({ taskId, mutateParam }: UseDeleteTaskProps) => {
      if (!mutateParam) {
        return;
      }
      if (mutateParam.isOverdue || !mutateParam.date) {
        await queryClient.cancelQueries({ queryKey: ['lifestack.todo.overdueTasks'] });
        queryClient.setQueriesData<TaskItem[]>(
          { queryKey: ['lifestack.todo.overdueTasks'] },
          (prevTasks) => {
            if (mutateParam?.parentOriginalTaskId) {
              // subtask
              return prevTasks?.map((task) => {
                if (task.originalTaskId === mutateParam?.parentOriginalTaskId) {
                  return {
                    ...task,
                    subTasks: task.subTasks?.filter((subtask) => subtask.taskId !== taskId),
                  };
                }
                return task;
              });
            }
            return prevTasks?.filter((task) => task.taskId !== taskId);
          }
        );
      } else {
        const queryKey = ['lifestack.todo.tasks', { dueDate: mutateParam.date }];
        await queryClient.cancelQueries({
          queryKey,
        });
        queryClient.setQueriesData<TasksByDate>({ queryKey }, (prevTasks) => {
          if (mutateParam?.parentOriginalTaskId) {
            // subtask
            return {
              date: prevTasks?.date,
              tasks: prevTasks?.tasks?.map((task) => {
                if (task.originalTaskId === mutateParam?.parentOriginalTaskId) {
                  return {
                    ...task,
                    subTasks: task.subTasks?.filter((subtask) => subtask.taskId !== taskId),
                  };
                }
                return task;
              }),
              updatedAt: new Date().toISOString(),
            };
          }
          return {
            date: prevTasks?.date,
            tasks: prevTasks?.tasks?.filter((task) => task.taskId !== taskId),
            updatedAt: new Date().toISOString(),
          };
        });
      }
    },
    onSuccess: (_, { mutateParam }) => {
      queryClient.invalidateQueries({ queryKey: TEAM_TASKS_QUERY_KEY, type: 'active' });

      if (!mutateParam) {
        queryClient.invalidateQueries({ queryKey: ['lifestack.todo.tasks'] });
        queryClient.invalidateQueries({ queryKey: ['lifestack.todo.overdueTasks'] });
        return;
      }

      if (mutateParam.isOverdue || !mutateParam.date) {
        queryClient.invalidateQueries({ queryKey: ['lifestack.todo.overdueTasks'] });
      } else {
        const queryKey = ['lifestack.todo.tasks', { dueDate: mutateParam.date }];

        queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  useEffect(() => {
    const error = (deleteTaskMutation.error as AxiosError)?.response;
    if (!error) {
      return;
    }
    if (error.status === 401) {
      setTaskErrorSnackbar({
        status: error.status,
        message: translateI18n('task.error.invalidToken'),
        provider: (error.data as TaskError401Res).provider as TodoIntegrationType,
        action: 'delete',
      });
      return;
    }
    setTaskErrorSnackbar({
      status: error.status,
      message: translateI18n('task.error.deleteFailed'),
      provider: (error.data as TaskError401Res).provider as TodoIntegrationType,
      action: 'delete',
    });
  }, [deleteTaskMutation.error]);

  return {
    deleteTask: deleteTaskMutation.mutateAsync,
    isDeleting: deleteTaskMutation.isPending,
    ...deleteTaskMutation,
  };
}
